import LinearProgress from '@material-ui/core/LinearProgress';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { isUndefined } from 'lodash';
import React from 'react';
import { divider, primary } from '../Styles/Colours';
import styles from './FullScreenModal.module.scss';
import { BackArrow, CloseIcon } from './Icons';
import { WindowEventListener } from './WindowEventListener';

export enum ScrollContainerMode {
    Narrow,
    FullWidth
}

export enum ModalAnimationStyle {
    OpenAndClose,
    OpenOnly,
    CloseOnly,
    None
}

const BorderLinearProgress = withStyles({
    root: {
        height: 8,
        backgroundColor: divider,
        width: '100%',
        position: 'fixed',
        zIndex: 1
    },
    bar: {
        backgroundColor: primary
    }
})(LinearProgress);

export function FullScreenModalInfo(props: { info: string | JSX.Element, unalignContent?: boolean }): JSX.Element {
    return <div className={classNames(styles.info, { [styles.unaligned]: props.unalignContent })}>{props.info}</div>;
}

export function FullScreenModalTitle(props: { title: string, unalignContent?: boolean }): JSX.Element {
    return <div className={classNames(styles.title, { [styles.unaligned]: props.unalignContent })}>{props.title}</div>;
}

export const FullScreenModal: React.FC<{
    header?: React.ReactNode;
    title: string;
    info: string | JSX.Element;
    nonInteractivePlaceholder?: React.ReactNode;
    onClose(): void;
    onBack?(): void;
    hideBackButton?: boolean;
    hideCloseButton?: boolean;
    progressPercent?: number;
    alertContent?: React.ReactNode;
    unalignContent?: boolean;
    /**
     * @deprecated In favour of `FullScreenModalButtonContainer`
     */
    buttons?: React.ReactNode;
    modalAnimationStyle?: ModalAnimationStyle;
}> = props => {
    const containerClasses = classNames(styles.container, {
        [styles.animationFull]:
            props.modalAnimationStyle === undefined || props.modalAnimationStyle === ModalAnimationStyle.OpenAndClose,
        [styles.animationOpenOnly]: props.modalAnimationStyle === ModalAnimationStyle.OpenOnly,
        [styles.animationCloseOnly]: props.modalAnimationStyle === ModalAnimationStyle.CloseOnly
    });

    return (
        <WindowEventListener
            onKeyDown={e => {
                if (props.nonInteractivePlaceholder) return;
                if (e.key === 'Escape') {
                    props.onClose();
                }
            }}
        >
            <div className={containerClasses}>
                {!props.nonInteractivePlaceholder && !isUndefined(props.progressPercent) && (
                    <BorderLinearProgress variant='determinate' color='secondary' value={props.progressPercent} />
                )}
                {!!props.hideBackButton === false && !props.nonInteractivePlaceholder && props.onBack && (
                    <div className={styles.backButton} onClick={props.onBack} data-test-id={`navigation-control-back`}>
                        <BackArrow />
                    </div>
                )}
                {!props.nonInteractivePlaceholder && (props.hideCloseButton !== true) && (
                    <div
                        className={styles.closeButton}
                        onClick={props.onClose}
                        data-test-id={`navigation-control-close`}
                    >
                        <CloseIcon />
                    </div>
                )}
                <div
                    className={classNames(styles.body, {
                        [styles.alignItemsCenter]: !props.unalignContent
                    })}
                >
                    {props.alertContent}
                    {props.nonInteractivePlaceholder ?? (
                        <>
                            {!!props.header && <div className={styles.header}>{props.header}</div>}
                            {!!props.title && <FullScreenModalTitle title={props.title} unalignContent={props.unalignContent} />}
                            {!!props.info && <FullScreenModalInfo info={props.info} unalignContent={props.unalignContent} />}
                            {props.children}
                            {props.buttons && (
                                <FullScreenModalButtonContainer>{props.buttons}</FullScreenModalButtonContainer>
                            )}
                        </>
                    )}
                </div>
            </div>
        </WindowEventListener>
    );
};

/**
 * @description Button container for FullScreenModal. Useful for positioning your buttons anywhere inside the modal.
 */
export const FullScreenModalButtonContainer: React.FC = ({ children }) => (
    <div className={styles.buttonContainer}>{children}</div>
);

export const FullScreenModalScrollContainer: React.FC<{
    mode?: ScrollContainerMode;
}> = props => {
    const mode = props.mode ?? ScrollContainerMode.Narrow;
    const containerClasses = classNames(styles.scrollContainer, {
        [styles.narrow]: mode === ScrollContainerMode.Narrow,
        [styles.fullWidth]: mode === ScrollContainerMode.FullWidth
    });

    return (
        <>
            <div className={containerClasses}>{props.children}</div>
        </>
    );
};
